/** Adapted from standalone-search f83fe2d1 via selected Registrar V2. See docs/delivery/iteration2/fusion-shared-kit/TRANSFER.json. */
"use client";

import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import type { Column, Table } from "@tanstack/react-table";
import { Settings2 as Settings } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";

export type DataTableViewOptionGroup = "identifiers" | "columns" | "more";

export interface DataTableViewOption {
  id: string;
  label: string;
  group?: DataTableViewOptionGroup;
}

interface DataTableViewOptionsProps<TData> {
  table: Table<TData>;
  options?: DataTableViewOption[];
  onReset?: () => void;
}

interface ResolvedViewOption<TData> extends DataTableViewOption {
  column: Column<TData>;
  group: DataTableViewOptionGroup;
}

function humanizeColumnId(id: string) {
  return id
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function ViewOptionItem<TData>({
  option,
}: {
  option: ResolvedViewOption<TData>;
}) {
  return (
    <DropdownMenuCheckboxItem
      checked={option.column.getIsVisible()}
      className="min-h-9"
      onCheckedChange={(value) => option.column.toggleVisibility(!!value)}
      onSelect={(event) => event.preventDefault()}
    >
      {option.label}
    </DropdownMenuCheckboxItem>
  );
}

export function DataTableViewOptions<TData>({
  table,
  options = [],
  onReset,
}: DataTableViewOptionsProps<TData>) {
  const configuredOptions = new Map(
    options.map((option) => [option.id, option]),
  );
  const resolvedOptions: ResolvedViewOption<TData>[] = table
    .getAllColumns()
    .filter((column) => column.getCanHide())
    .map((column) => {
      const configured = configuredOptions.get(column.id);
      return {
        column,
        id: column.id,
        label: configured?.label ?? humanizeColumnId(column.id),
        group: configured?.group ?? "more",
      };
    });

  const identifiers = resolvedOptions.filter(
    (option) => option.group === "identifiers",
  );
  const columns = resolvedOptions.filter(
    (option) => option.group === "columns",
  );
  const more = resolvedOptions.filter((option) => option.group === "more");

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="ml-auto"
          aria-label="Columns"
        >
          <Settings className="size-4" aria-hidden="true" />
          View
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[min(18rem,calc(100vw-1rem))] p-2"
      >
        <DropdownMenuLabel className="px-2 pb-2 pt-1 text-body">
          Display
        </DropdownMenuLabel>

        {identifiers.length > 0 && (
          <>
            <DropdownMenuLabel className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Identifiers
            </DropdownMenuLabel>
            {identifiers.map((option) => (
              <ViewOptionItem key={option.id} option={option} />
            ))}
            <DropdownMenuSeparator />
          </>
        )}

        {columns.length > 0 && (
          <>
            <DropdownMenuLabel className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Columns
            </DropdownMenuLabel>
            {columns.map((option) => (
              <ViewOptionItem key={option.id} option={option} />
            ))}
          </>
        )}

        {more.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="min-h-9">
                More fields
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-52 p-1">
                {more.map((option) => (
                  <ViewOptionItem key={option.id} option={option} />
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}

        <DropdownMenuSeparator />
        <div className="flex items-center justify-between gap-2">
          <DropdownMenuItem
            className="flex-1 justify-start text-muted-foreground"
            onSelect={() => table.toggleAllColumnsVisible(true)}
          >
            Show all
          </DropdownMenuItem>
          <DropdownMenuItem
            className="justify-end text-muted-foreground"
            onSelect={() =>
              onReset ? onReset() : table.resetColumnVisibility()
            }
          >
            Reset
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
